<script setup lang="ts">
import { computed } from 'vue';
import { genHash } from '@/core/utils/route';
import { TopPopupListItem } from '@/components/popup/popup';
import { useTabsStore } from './store';
import type { PropsMenuItem } from './types';

const props = withDefaults(defineProps<PropsMenuItem>(), {
	scrollable: true,
	component: undefined,
});

const store = useTabsStore();

const href = computed(() => {
	// это внутренняя хеш навигация, ссылка на вкладку
	if (props.name && store.$id) {
		return genHash(store.$id, props.name);
	}

	return props.href;
});

const tagName = computed(() => {
	if (store.showMenuInPopup) {
		return TopPopupListItem;
	}

	return href ? 'a' : 'button';
});

/**
 * Клик по элементу меню
 */
const onClick = (e: MouseEvent) => {
	// это внутренняя хеш навигация, ссылка на вкладку
	if (!props.href && href.value) {
		e.preventDefault();
	}

	if (props.name) {
		store.activeItemName = props.name;
	}
};
</script>

<template>
	<component
		:is="tagName"
		target="_self"
		:class="{
			['top-tabsView_menuItem']: !store.showMenuInPopup,
			['top-active']: name && store.activeItemName === name,
			['top-disabled']: disabled,
			['top-spa-disabled']: true,
		}"
		:href="href"
		:data-top-icon="icon || undefined"
		:disabled="disabled || undefined"
		@click="onClick"
	>
		<slot name="default" v-if="store.showMenuInPopup"></slot>

		<span
			v-else-if="$slots.default && !store.isShort"
			class="top-ellipsis"
		>
			<slot name="default"></slot>
		</span>
	</component>
</template>

<style>
.top-tabsView_menuItem {
	--top-icon-color: var(--color-text-2);
	--top-icon-width: calc(var(--top-icon-size));

	cursor: pointer;
	box-sizing: border-box;

	border: none;
	border-radius: var(--top-radius-2);
	padding: var(--top-padding-2);
	background: transparent;
	height: 40px;

	color: var(--color-text-1);
	text-decoration: none;
	white-space: nowrap;

	display: flex;
	gap: var(--top-gap-2);
	align-items: center;
	justify-content: flex-start;
	transition: background var(--transition);
}

.top-tabsView_menuItem:hover {
	--top-icon-color: var(--color-text-1);

	background: var(--color-bg-shading-1);
	text-decoration: none;
}

.top-tabsView_menuItem.top-active {
	--top-icon-color: inherit;

	background: var(--color-layout-front-1);
	color: inherit;
}

.top-tabsView_menuItem:disabled:not(option):not(optgroup):not(.top-forms-option),
.top-tabsView_menuItem:disabled ~ .top-formsCaption,
.top-tabsView_menuItem.top-disabled[data-top-icon]:before,
.top-tabsView_menuItem.top-disabled[data-top-icon2]:after {
	opacity: unset;
	filter: unset;
}

.top-tabsView_menuItem.top-disabled {
	--top-icon-color: var(--color-text-4);

	color: var(--color-text-4);
}
</style>
